<template>
  <div class="event-competition-edits">
    <v-card
      v-edit-vertical-line="{
        index,
        targetSelector: '.event-competition-edits'
      }"
      color="primary"
      class="fipa--header d-print-none"
      tile
    >
      <v-row no-gutters align="center" class="header-lower-font d-print-none">
        <v-col :cols="12" class="d-flex" style="justify-content: space-between">
          <div style="align-self: center">
            <slot name="subheading">
              {{ $dayjs.date(state.competitionDay, "formal") }}
            </slot>
          </div>

          <div
            style="
              display: flex;
              align-items: center;
              justify-content: flex-end;
            "
          >
            <AppShowCompetitionsStarts
              :competitions="competitionsSortedByCode"
              :event-limit="state.eventLimit"
            />

            <template v-if="state.canLimitPerDay">
              <v-btn-icon
                class="btn-with-icon mx-2 px-0"
                variant="text"
                width="24px"
                height="24px"
                :aria-label="$t('common:editDailyLimitButton.alt')"
                @click="actions.openLimitEntriesForm"
              >
                <div class="bg-edit-green btn-icon-with-text mr-0">
                  <v-icon size="small" class="text-white">$edit</v-icon>
                </div>
              </v-btn-icon>
              <span v-if="state.canAdd" class="mx-2">|</span>
            </template>
            <v-btn
              v-if="state.canAdd"
              id="newCompetition"
              class="btn-with-icon text-white"
              variant="text"
              slim
              :to="{
                name: 'CompetitionEventCreationPage',
                params: { eventId: state.eventId },
                query: { startDate: state.competitionDay }
              }"
              data-test="eventNewCompetition"
            >
              <div class="bg-edit btn-icon-with-text">
                <v-icon size="12px" class="text-white">$iconPlus</v-icon>
              </div>
              <span class="text-white">
                {{ $t("common:button.add", "Add") }}
              </span>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <div class="d-none d-print-block">
      <slot name="subheading">
        {{ $dayjs.date(state.competitionDay, "formal") }}
      </slot>
    </div>

    <div v-if="state.canLimitPerDay" v-show="state.limitEntriesForm">
      <slot name="entryLimitForm" />
    </div>

    <table class="entsys--table">
      <thead class="text-left">
        <tr style="font-weight: bold">
          <th style="width: 17em">
            <slot name="competitions-column">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.competitions.column",
                  "Competition",
                  "Competition column label in competitions section on event page"
                )
              }}
            </slot>
          </th>
          <th style="width: 16em">
            <slot name="judgments-column">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.judgment.column",
                  "Judgement and difficulty",
                  "Judgement and difficulty column label in competitions section on event page"
                )
              }}
            </slot>
          </th>
          <th style="width: 19em">
            <slot name="prizes-column">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.prizes.column",
                  "Prizes",
                  "Prizes column label in competitions section on event page"
                )
              }}
            </slot>
          </th>
          <th style="width: 14em">
            <slot name="openFor-column">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.OpenFor.column",
                  "Open for",
                  "Open for column label in competitions section on event page"
                )
              }}
            </slot>
          </th>
          <th class="text-center">
            <slot name="registered-column">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.registered.column",
                  "Registered",
                  "Registered column label in competitions section on event page"
                )
              }}
            </slot>
          </th>
          <th class="text-right" colspan="2">
            <slot name="fees-column">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.fees.column",
                  "Fees",
                  "Fees column label in competitions section on event page"
                )
              }}
            </slot>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-if="competitionsSortedByCode.length == 0">
          <td colspan="99" class="text-center">
            <span class="text-muted">
              {{
                $t(
                  "events:EventPage.CompetitionsSection.Nocompetitions.message",
                  "No competitions entered"
                )
              }}
            </span>
          </td>
        </tr>

        <template
          v-for="(item, i) in competitionsSortedByCode"
          :key="i + '-row'"
        >
          <tr
            :data-scroll-focus="'competition-' + item.id"
            :data-test="`competition-${item.code}`"
          >
            <td>
              <AppShowCompetitionCodeAndTags :competition="item">
                {{ item.code }}
                <AppShowLockState
                  v-if="item.publicRegistrationsDisabled"
                  :lock-state="item.publicRegistrationsDisabled"
                  class="ml-1"
                />
              </AppShowCompetitionCodeAndTags>
              <AppShowCompetitionName :competition="item" />
              <AppShowCupSeasons
                v-if="item.cupSeasons.length > 0"
                :cup-seasons="item.cupSeasons"
              />

              <div v-if="item.competitionLevelId" class="text-secondary">
                {{ actions.get(item.competitionLevel, "name") }}
              </div>
              <div v-if="item.eventcodeId">
                <AppShowEventcode :competition="item" />
              </div>
            </td>
            <td>
              <div>
                {{ actions.get(item.competitionRule, "name") }}
                {{ actions.isIndoor(item) }}
              </div>
              <div>
                {{ actions.get(item.competitionRule, "description") }}
              </div>
              <AppShowDifficultyLevel :competition="item" />
            </td>
            <td>
              <span v-if="item.prizeDistribution">
                <AppShowPrizeDistribution
                  :prize-distribution="item.prizeDistribution"
                />
                <br v-if="item.prizePoolCents" />
              </span>
              <span v-if="item.prizePoolCents" class="currency-display">
                <span>{{ actions.masterCurrency(item.prizePoolCents) }}</span>
                {{
                  $t(
                    "events:EventPage.CompetitionsSection.prizes.secondarytext",
                    "in total",
                    "secondary text for showing prizes in competitions section on event page"
                  )
                }}
                <br v-if="item.prizeText" />
              </span>
              <span v-if="item.prizeText">
                {{ item.prizeText }}
              </span>
            </td>
            <td>
              <span
                class="force-paragraph-no-margin"
                v-html="item.description"
              />
              {{ actions.isRestricted(item) }}
              {{ actions.isTeamCompetition(item) }}
              {{ actions.outsideOfCompetitionAllowed(item) }}

              <AppShowSections v-if="item.sections" :sections="item.sections" />
            </td>
            <td class="text-center">
              <AppShowClassRegistrationsCount
                :value="item"
                style="cursor: pointer"
                @click="actions.openClassParticipantList(item.id)"
              />
            </td>
            <td style="width: 169px" class="text-right">
              <AppShowCompetitionFees :comp="item" />
              <div v-if="item.externalScheduleUrl">
                <v-btn
                  :href="item.externalScheduleUrl"
                  target="_blank"
                  variant="text"
                  class="px-0"
                >
                  {{
                    $t(
                      "events:EventPage.CompetitionsSection.viewSchedule.text",
                      "View schedule"
                    )
                  }}
                  <v-icon size="small" class="ml-2">$iconArrowOutSquare</v-icon>
                </v-btn>
              </div>
              <small v-if="item.registrationEndDate">
                <span class="text-red"
                  >{{
                    $t(
                      "events:EventPage.CompetitionsSection.competitionRegistrationEndDate.text",
                      "Registration end date:"
                    )
                  }}
                  {{ $dayjs.date(item.registrationEndDate) }}</span
                >
              </small>
            </td>
            <td
              v-if="
                $allowedMutation(item.allowedMutations, [
                  'eventCompetitionSplit',
                  'eventCompetitionClone',
                  'eventCompetitionCreateOrUpdate',
                  'eventCompetitionDelete',
                  'competitionUpdateRegistrationEndDateOverride'
                ]) ||
                actions.anyDrivingFields(item) ||
                $allowedMutation(
                  item.allowedFields,
                  'publicRegistrationRulesDescriptions'
                )
              "
              class="text-right actions-space d-print-none"
              style="padding: 0.55em 8px; width: 185px"
            >
              <v-row no-gutters justify="center">
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'competitionUpdateRegistrationEndDateOverride'
                    )
                  "
                  color="edit"
                  class="ma-1"
                  data-test="eventCompetitionRegistrationOverwrite"
                  size="small"
                  icon
                  :disabled="state.mutationLoading"
                  :loading="state.mutationLoading"
                  @click="
                    actions.openCompetitionRegistrationDateOverwriteForm(item)
                  "
                >
                  <v-icon size="small" color="white">$iconCalendarClock</v-icon>
                </v-btn-icon>

                <template
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'eventCompetitionSplit'
                    )
                  "
                >
                  <v-tooltip location="bottom">
                    <template #activator="{ props }">
                      <div v-bind="props">
                        <v-btn-icon
                          color="edit"
                          size="small"
                          class="ma-1"
                          data-test="eventCompetitionSplit"
                          :aria-label="
                            $t('events:EventPage.Schedule.SplitButton.tooltip')
                          "
                          :disabled="state.mutationLoading"
                          :loading="state.mutationLoading"
                          @click="actions.splitCompetition(item)"
                        >
                          <v-icon size="small" class="text-white">
                            $iconSplit
                          </v-icon>
                        </v-btn-icon>
                      </div>
                    </template>
                    <span>{{
                      $t("events:EventPage.Schedule.SplitButton.tooltip")
                    }}</span>
                  </v-tooltip>
                </template>

                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'eventCompetitionClone'
                    )
                  "
                  color="edit"
                  data-test="eventCompetitionClone"
                  size="small"
                  class="ma-1"
                  :aria-label="
                    $t('events:Competition.cloneCompetitionButton.alt')
                  "
                  :disabled="state.mutationLoading"
                  :loading="state.mutationLoading"
                  @click="actions.cloneCompetition(item)"
                >
                  <v-icon size="small" class="text-white"> $iconCopy </v-icon>
                </v-btn-icon>
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'eventCompetitionCreateOrUpdate'
                    )
                  "
                  :to="{
                    name: 'CompetitionEditPage',
                    params: { eventId: state.eventId, competitionId: item.id },
                    state: {
                      returnRoute: {
                        name: 'EventSchedule',
                        hash: '#classes',
                        params: { eventId: state.eventId },
                        state: {
                          routeScrollState: {
                            querySelector: `[data-scroll-focus='competition-${item.id}']`
                          }
                        }
                      }
                    }
                  }"
                  color="edit-green"
                  :aria-label="$t('common:editButton.alt')"
                  :disabled="state.mutationLoading"
                  class="ma-1"
                  data-test="eventEditCompetition"
                  size="small"
                >
                  <v-icon color="white" size="small">$edit</v-icon>
                </v-btn-icon>
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'eventCompetitionDelete'
                    )
                  "
                  size="small"
                  :disabled="state.mutationLoading"
                  :aria-label="$t('common:deleteButton.alt')"
                  color="remove"
                  class="ma-1"
                  data-test="eventCompetitionDelete"
                  @click="actions.deleteCompetition(item.id, $event)"
                >
                  <v-icon size="small" color="white"> $iconTrash </v-icon>
                </v-btn-icon>
              </v-row>
              <div class="text-center">
                <v-btn-expand
                  v-if="
                    actions.anyDrivingFields(item) ||
                    $allowedMutation(
                      item.allowedFields,
                      'publicRegistrationRulesDescriptions'
                    )
                  "
                  class="ma-1"
                  :model-value="state.expanded.includes(item.id)"
                  data-test="expand"
                  :data-competition-selector="`competition-expand-button-${item.id}`"
                  @click="actions.expand(item)"
                >
                </v-btn-expand>
              </div>
            </td>
          </tr>

          <tr v-if="state.expanded.includes(item.id)" :key="i + '-expand'">
            <td colspan="99" class="pa-0">
              <v-card
                color="strong"
                class="pa-6 elevation-1"
                :data-test="`additionaFields-${item.code}`"
              >
                <h3 v-if="item.possiblePhases && item.possiblePhases.length">
                  {{
                    $t(
                      "events:EventPage.CompetitionsSection.driving.CompPhases.secondarytext"
                    )
                  }}
                  <span style="font-weight: normal">{{
                    actions.competitionPhases(item)
                  }}</span>
                </h3>

                <template v-if="actions.anyDrivingFields(item)">
                  <v-divider class="my-3" />
                  <v-row dense>
                    <v-col v-if="actions.dressageFields(item)" cols="12" md="4">
                      <h3>
                        {{
                          $t(
                            "events:EntryForm.driving.CompSection.dressage.label",
                            "Dressage",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                      </h3>
                      <div v-if="item.dressageArenaSize">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext4",
                            "Size of arena:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{
                          item.dressageArenaSize == "undefined"
                            ? $t(
                                "events:CompetitionForm.input.driving.undefinedSize.text",
                                "Undefined"
                              )
                            : item.dressageArenaSize
                        }}
                      </div>
                      <div v-if="item.dressageArenaGround">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext2",
                            "Type of ground:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ actions.arenaGround(item.dressageArenaGround) }}
                      </div>
                    </v-col>
                    <v-col v-if="actions.marathonFields(item)" cols="12" md="4">
                      <h3>
                        {{
                          $t(
                            "events:EntryForm.driving.CompSection.marathon.label",
                            "Marathon",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                      </h3>
                      <div v-if="item.marathonArenaSize">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext4",
                            "Size of arena:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ item.marathonArenaSize }}
                      </div>
                      <div v-if="item.marathonArenaGround">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext2",
                            "Type of ground:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ actions.arenaGround(item.marathonArenaGround) }}
                      </div>
                      <div v-if="item.marathonArenaHasWater != null">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext5",
                            "Water:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{
                          item.marathonArenaHasWater
                            ? $t("common:yes.label")
                            : $t("common:no.label")
                        }}
                      </div>
                      <div v-if="item.marathonNumberOfObstacles">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext6",
                            "Number of obstacles:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ item.marathonNumberOfObstacles }}
                      </div>
                    </v-col>
                    <v-col v-if="actions.conesFields(item)" cols="12" md="4">
                      <h3>
                        {{
                          $t(
                            "events:EntryForm.driving.CompSection.cones.label",
                            "Cones",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                      </h3>
                      <div v-if="item.conesArenaSize">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext4",
                            "Size of arena:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{
                          item.conesArenaSize == "undefined"
                            ? $t(
                                "events:CompetitionForm.input.driving.undefinedSize.text",
                                "Undefined"
                              )
                            : item.conesArenaSize
                        }}
                      </div>
                      <div v-if="item.conesArenaGround">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext2",
                            "Type of ground:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ actions.arenaGround(item.conesArenaGround) }}
                      </div>
                      <div v-if="item.conesClearance">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext8",
                            "Clearance:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ item.conesClearance }} cm
                      </div>
                      <div v-if="item.conesJudgement">
                        {{
                          $t(
                            "events:EventPage.CompetitionsSection.driving.secondarytext9",
                            "Judgement:",
                            "secondary text for showing driving specific components in competitions section on event page"
                          )
                        }}
                        {{ item.conesJudgement }}
                      </div>
                    </v-col>
                  </v-row>
                </template>

                <template
                  v-if="
                    $allowedMutation(
                      item.allowedFields,
                      'publicRegistrationRulesDescriptions'
                    )
                  "
                >
                  <v-divider
                    v-if="
                      actions.anyDrivingFields(item) ||
                      (item.possiblePhases && item.possiblePhases.length)
                    "
                    class="my-3"
                  />
                  <CompetitionEntryRules :competition="item" />
                </template>
              </v-card>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
<script>
import { defineComponent } from "vue";

import AppShowDifficultyLevel from "@AppShows/AppShowDifficultyLevel.vue";
import AppShowPrizeDistribution from "@/components/elements/AppShows/AppShowPrizeDistribution.vue";
import AppShowClassRegistrationsCount from "@AppShows/event/AppShowClassRegistrationsCount.vue";
import AppShowSections from "@AppShows/event/AppShowSections.vue";
import AppShowCompetitionFees from "@AppShows/event/AppShowCompetitionFees.vue";

import AppShowCompetitionsStarts from "@/components/elements/AppShows/event/AppShowCompetitionsStarts.vue";
import AppShowLockState from "@AppShows/event/AppShowLockState.vue";

import AppShowEventcode from "@AppShows/event/AppShowEventcode.vue";
import AppShowCompetitionName from "@AppShows/event/AppShowCompetitionName.vue";

import CompetitionEntryRules from "./CompetitionEntryRules.vue";
import AppShowCompetitionCodeAndTags from "@/components/elements/AppShows/event/AppShowCompetitionCodeAndTags.vue";
import editVerticalLine from "@/directives/edit-vertical-line";
import AppShowCupSeasons from "@AppShows/event/AppShowCupSeasons.vue";

export default defineComponent({
  name: "EventCompetitionsDesktop",
  directives: { editVerticalLine },
  components: {
    AppShowCompetitionFees,
    AppShowDifficultyLevel,

    AppShowSections,
    AppShowPrizeDistribution,
    AppShowClassRegistrationsCount,

    AppShowCompetitionsStarts,
    AppShowLockState,

    AppShowEventcode,
    AppShowCompetitionName,

    CompetitionEntryRules,
    AppShowCompetitionCodeAndTags,
    AppShowCupSeasons
  },
  props: {
    index: { type: Number, required: true },
    competitionsSortedByCode: { type: Array, required: true },
    state: { type: Object, required: true },
    actions: { type: Object, required: true }
  }
});
</script>
